import React from 'react';
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// CSS Styles for Stripe-style dual ring animation (CSS Only)
const styles = `
  @keyframes checkmark-draw {
    0% { stroke-dashoffset: 24; }
    100% { stroke-dashoffset: 0; }
  }
  @keyframes ring-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  @keyframes connector-extend {
    0% { transform: scaleX(0); }
    100% { transform: scaleX(1); }
  }
  .checkmark-animate {
    stroke-dasharray: 24;
    stroke-dashoffset: 24;
    animation: checkmark-draw 0.4s ease-out forwards;
  }
  .ring-pulse {
    animation: ring-pulse 1.5s ease-in-out infinite;
  }
  .connector-line {
    transform-origin: left center;
  }
`;

interface ProgressStepIndicatorProps {
  currentStep: 'converting' | 'uploading' | 'done' | 'error';
  progress: number; // 0-100
  onClose?: () => void;
}

/**
 * Mobile-optimized Mini Progress Ring (CSS Only)
 * Renders a small ring inside buttons
 */
export const MiniProgressRing: React.FC<{
  progress: number;
  size?: number;
  stroke?: number;
  color?: string;
  trackColor?: string;
  className?: string;
}> = ({ progress, size = 20, stroke = 2, color = 'currentColor', trackColor, className = '' }) => {
  const radius = (size - stroke) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  // Use trackColor if provided, otherwise use color with opacity
  const actualTrackColor = trackColor || color;

  return (
    <div className={`relative flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke={actualTrackColor}
          strokeOpacity={trackColor ? 1 : 0.2}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.3s ease' }}
        />
      </svg>
    </div>
  );
};

/**
 * Single Ring Component for Step
 */
const StepRing: React.FC<{
  progress: number;
  isComplete: boolean;
  isError: boolean;
  isActive: boolean;
  size?: number;
}> = ({ progress, isComplete, isError, isActive, size = 64 }) => {
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  // Colors
  const trackColor = '#E5E5E5';
  const progressColor = isError ? '#EF4444' : isComplete ? '#6FEDD6' : '#1A1A1A';
  const bgColor = isComplete ? '#6FEDD6' : isError ? '#FEE2E2' : 'white';

  return (
    <div 
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      {/* SVG Ring */}
      <svg width={size} height={size} className="absolute transform -rotate-90">
        {/* Background Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke={trackColor}
          strokeWidth={stroke}
        />
        {/* Progress Arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke={progressColor}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={isActive && !isComplete ? 'ring-pulse' : ''}
          style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
        />
      </svg>

      {/* Center Content */}
      <div 
        className="absolute rounded-full flex items-center justify-center border-2 border-black"
        style={{ 
          width: size - 16, 
          height: size - 16,
          backgroundColor: bgColor,
          transition: 'background-color 0.3s ease'
        }}
      >
        {isComplete ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 13l4 4L19 7"
              stroke="#1A1A1A"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="checkmark-animate"
            />
          </svg>
        ) : isError ? (
          <X className="w-5 h-5 text-red-500" strokeWidth={3} />
        ) : (
          <span className="text-sm font-bold tabular-nums">
            {Math.round(progress)}%
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Desktop Full Progress Overlay with Stripe-style Dual Ring Animation (CSS Only)
 */
export const ProgressStepIndicator: React.FC<ProgressStepIndicatorProps> = ({
  currentStep,
  progress,
  onClose
}) => {
  const { t } = useTranslation();
  // Inject styles once
  React.useEffect(() => {
    if (!document.getElementById('progress-step-styles')) {
      const style = document.createElement('style');
      style.id = 'progress-step-styles';
      style.innerHTML = styles;
      document.head.appendChild(style);
    }
  }, []);

  const isConverting = currentStep === 'converting';
  const isUploading = currentStep === 'uploading';
  const isDone = currentStep === 'done';
  const isError = currentStep === 'error';

  // Calculate progress for each ring
  // Ring 1 (Converting): Active during converting, complete when uploading/done
  // Ring 2 (Uploading): Active during uploading, complete when done
  const ring1Progress = isConverting ? progress : (isUploading || isDone || isError) ? 100 : 0;
  const ring1Complete = isUploading || isDone;
  
  const ring2Progress = isUploading ? progress : isDone ? 100 : 0;
  const ring2Complete = isDone;

  // Connector line visibility (shows after converting is done)
  const showConnector = isUploading || isDone || isError;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center max-w-md w-full mx-4 relative overflow-hidden border-2 border-black">
        
        {/* Close button for Error/Done */}
        {(isDone || isError) && (
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-neutral-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Dual Ring Container */}
        <div className="flex items-center justify-center gap-4 mb-6">
          {/* Ring 1 - Converting */}
          <div className="flex flex-col items-center gap-2">
            <StepRing
              progress={ring1Progress}
              isComplete={ring1Complete}
              isError={isError && isConverting}
              isActive={isConverting}
              size={64}
            />
            <span className={`text-xs font-medium ${isConverting ? 'text-black' : 'text-neutral-400'}`}>
              {t("teleprompter.recording.converting")}
            </span>
          </div>

          {/* Connector Line */}
          <div className="flex items-center" style={{ marginBottom: 20 }}>
            <div 
              className="h-1 bg-neutral-200 rounded-full overflow-hidden"
              style={{ width: 48 }}
            >
              <div 
                className={`h-full rounded-full connector-line ${isError ? 'bg-red-500' : 'bg-[#6FEDD6]'}`}
                style={{
                  width: '100%',
                  transform: showConnector ? 'scaleX(1)' : 'scaleX(0)',
                  transition: 'transform 0.5s ease-out'
                }}
              />
            </div>
          </div>

          {/* Ring 2 - Uploading */}
          <div className="flex flex-col items-center gap-2">
            <StepRing
              progress={ring2Progress}
              isComplete={ring2Complete}
              isError={isError && isUploading}
              isActive={isUploading}
              size={64}
            />
            <span className={`text-xs font-medium ${isUploading ? 'text-black' : 'text-neutral-400'}`}>
              {t("teleprompter.recording.uploading")}
            </span>
          </div>
        </div>

        {/* Text Status */}
        <h3 className="text-xl font-bold mb-2 text-center">
          {isConverting && t("teleprompter.recording.convertingTitle")}
          {isUploading && t("teleprompter.recording.uploadingTitle")}
          {isDone && t("teleprompter.recording.uploadSuccess")}
          {isError && t("teleprompter.recording.uploadFailed")}
        </h3>
        
        <p className="text-neutral-500 text-center text-sm">
          {isConverting && t("teleprompter.recording.convertingDesc")}
          {isUploading && t("teleprompter.recording.uploadingDesc")}
          {isDone && t("teleprompter.recording.uploadSuccessDesc")}
          {isError && t("teleprompter.recording.uploadFailedDesc")}
        </p>

        {/* Done button */}
        {isDone && (
          <button
            onClick={onClose}
            className="mt-6 px-6 py-2 bg-[#6FEDD6] text-black font-bold border-2 border-black rounded-xl hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[4px_4px_0_#1A1A1A] transition-all duration-100"
          >
            <Check className="w-4 h-4 inline-block mr-2" />
            {t("teleprompter.recording.done")}
          </button>
        )}
      </div>
    </div>
  );
};
